import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators';
import store from '../index';
import { brandCenterMockData } from '../mock.data';
import {
  CarouselSlide, AssetSection, BrandWrite, BroadcastEvent,
} from '../../common/types';
import BrandService from '../../common/api/brand.service';
import Brand from '../../common/model/Brand';
import Admin from '../../common/model/Admin';
import BrandAsset from '../../common/model/BrandAsset';
import FileUploader from '../../common/fileUploader';
import productModule from './product.module';

@Module({ dynamic: true, store, name: 'brand' })
class BrandModule extends VuexModule {
  splashSlides: Array<CarouselSlide> = brandCenterMockData.splashSlides;

  assetSections: Array<AssetSection> = brandCenterMockData.sectionPages;

  brand: Brand | null = null;

  admins: Array<Admin> = [];

  assets: Array<BrandAsset> = [];

  meetings: Array<BroadcastEvent> = [];

  service = new BrandService();

  subBrands: Array<Brand> = [];

  // Set brand when budtender visits brand's site, or when admin enters portal.
  @Mutation
  SET_BRAND(brand: Brand) {
    this.brand = brand;
  }

  @Mutation
  ADD_SUB_BRAND(brand: Brand) {
    this.subBrands.push(brand);
  }

  @Mutation
  SET_SUB_BRAND(subbrands: Array<Brand>) {
    this.subBrands = subbrands;
  }

  @Mutation
  SET_BRAND_ADMINS(admins: Array<Admin>) {
    this.admins = admins;
  }

  @Mutation
  SET_BRAND_ASSETS(assets: Array<BrandAsset>) {
    this.assets = assets;
  }

  @Mutation
  SET_BRAND_MEETINGS(assets: Array<BroadcastEvent>) {
    this.meetings = assets;
  }

  @Mutation
  ADD_MEETING(meeting: BroadcastEvent) {
    this.meetings.push(meeting);
  }

  @Mutation
  ADD_BRAND_ASSET(asset: BrandAsset) {
    this.assets.push(asset);
  }

  @Mutation
  REMOVE_BRAND_ASSET(assetId: string): void {
    this.assets = this.assets.filter(asset => asset.id !== assetId);
  }

  @Action
  async createBrand(payload: any): Promise<void> {
    const brandWrite: BrandWrite = payload.brand;
    const response = await this.service.postBrand(brandWrite);

    // Check to see if any errors occured.
    if (!response) {
      return;
    }

    // Unwrap brand from API, and upload file using the signed S3 URL
    const { brand } = response.data.data;
    const uploader = new FileUploader();
    const { logo } = payload;
    await uploader.sendToS3(logo, brand.logo);


    this.SET_BRAND(brand);
    this.SET_BRAND_ADMINS(brand.admins);
    this.SET_BRAND_ASSETS(brand.brand_assets);
  }


  @Action
  async createSubBrand(payload: any): Promise<void> {
    const brandWrite: BrandWrite = payload.brand;
    const response = await this.service.postBrand(brandWrite);

    // Check to see if any errors occured.
    if (!response) {
      return;
    }

    // Unwrap brand from API, and upload file using the signed S3 URL
    const { brand } = response.data.data;
    const uploader = new FileUploader();
    const { logo } = payload;
    await uploader.sendToS3(logo, brand.logo);
    this.ADD_SUB_BRAND(brand);
  }


  @Action
  async createBrandAsset(payload: any): Promise<void> {
    const response = await this.service.createBrandAsset(payload.asset);
    if (!response) {
      return;
    }

    // If no errors were thrown, upload the asset to S3 using the signed URL
    const { asset } = response.data.data;
    const uploader = new FileUploader();
    await uploader.sendToS3(payload.assetFile, asset.full_size_url);

    // Finally, add the brand asset to the current state.
    this.ADD_BRAND_ASSET(response.data.data.asset);
  }

  @Action
  async updateBrandAsset(payload: any): Promise<void> {
    const response = await this.service.updateBrandAsset(payload.asset);
    if (!response) {
      return;
    }

    const brandAsset = response.data.data.asset;

    if (!payload.assetFile) {
      return;
    }
    const uploader = new FileUploader();
    await uploader.sendToS3(payload.assetFile, brandAsset.full_size_url);
  }

  @Action
  async updateBrand(payload: any): Promise<void> {
    const { brand } = payload;
    if (payload.logo) {
      brand.logo = payload.logo.name;
    }
    if (payload.icon) {
      brand.icon = payload.icon.name;
    }
    const { banners } = payload;
    brand.banner_urls = [];
    if (banners.length > 0) {
      // If banners are set, write an array of the file names to brand.banners
      for (let bannerIndex = 0; bannerIndex < banners.length; bannerIndex += 1) {
        brand.banner_urls.push(banners[bannerIndex].name);
      }
    }

    const response = await this.service.updateBrand({ brand });
    if (!response) {
      return;
    }

    // If request has no errors, set brand to the updated fetched brand.
    this.SET_BRAND(response.data.data.brand);
    const uploader = new FileUploader();

    // If logo is set, upload it to S3.
    if (payload.logo) {
      await uploader.sendToS3(payload.logo, response.data.data.brand.logo);
      return;
    }

    // If icon is set, upload it to S3.
    if (payload.icon) {
      await uploader.sendToS3(payload.icon, response.data.data.brand.icon);
      return;
    }

    // Check to see if any banners were set in the update.
    if (banners.length === 0) {
      return;
    }

    // Banners are set, now we need to loop through them and upload to S3.
    const uploadPromises: Array<Promise<void>> = [];
    for (let bannerIndex = 0; bannerIndex < banners.length; bannerIndex += 1) {
      const signedS3Url: string = response.data.data.brand.banner_urls[bannerIndex];
      const bannerFile: File = banners[bannerIndex];
      uploadPromises.push(uploader.sendToS3(bannerFile, signedS3Url));
    }
    await Promise.all(uploadPromises);
  }

  @Action
  async fetchBrandForBudtender(brandId: string): Promise<void> {
    const response = await this.service.fetchBrandForBudtender(brandId);
    if (!response) {
      return;
    }
    const { brand } = response.data.data;
    this.SET_BRAND(brand);
    if (brand.sub_brands && brand.sub_brands.length > 0) {
      this.SET_SUB_BRAND(brand.sub_brands);
    }
    this.SET_BRAND_ADMINS(brand.admins);
    productModule.SET_PRODUCTS(brand.products);
    this.SET_BRAND_ASSETS(brand.brand_assets);
    this.SET_BRAND_MEETINGS(brand.meetings);
  }

  @Action
  async fetchBrand(brandId: string): Promise<void> {
    const response = await this.service.fetchBrand(brandId);
    if (!response) {
      return;
    }
    const { brand } = response.data.data;
    this.SET_BRAND(brand);
    if (brand.sub_brands && brand.sub_brands.length > 0) {
      this.SET_SUB_BRAND(brand.sub_brands);
      this.ADD_SUB_BRAND(brand);
    }
    this.SET_BRAND_ADMINS(brand.admins);
    productModule.SET_PRODUCTS(brand.products);
    this.SET_BRAND_ASSETS(brand.brand_assets);
    this.SET_BRAND_MEETINGS(brand.meetings);
  }

  @Action
  async emailBrandAsset(payload): Promise<void> {
    await this.service.emailBrandAsset(payload);
  }

  @Action
  async createMeeting(payload): Promise<void> {
    const response = await this.service.createMeeting(payload);
    if (!response) { return; }
    const meeting = response.data.data;
    this.ADD_MEETING(meeting);
  }

  @Action
  async deleteBrandAsset(brandAssetId: string): Promise<void> {
    const response = this.service.deleteBrandAsset(brandAssetId);
    if (!response) { return; }
    this.REMOVE_BRAND_ASSET(brandAssetId);
  }

  @Action
  async updateMeeting(payload): Promise<void> {
    await this.service.updateMeeting(payload);
  }
}

export default getModule(BrandModule);
